import { logManager } from '../logs'

// Models that this manager directly manages.
import Media from '../models/media'

// Libraries.
import EventEmitter from 'eventemitter3'

/**
 * Manager for Media objects.
 * Allows the creation and retrieval of media objects.
 * @class MediaManager
 */
export default function MediaManager (managers) {
  const log = logManager.getLogger('Manager', 'Media')

  // Internal variables.
  const trackManager = managers.trackManager
  const medias = new Map()
  const emitter = new EventEmitter()

  /**
   * Retrieve a snapshot of all Media objects' current state.
   * @method getState
   * @return {Array}
   */
  function getState () {
    return getAll().map(id => get(id).getState())
  }

  /**
   * Workaround to get Firefox to behave similarly to Chrome regarding device permission prompts.
   * @method browserConstraintsWorkaround
   * @param  {MediaStreamConstraints}  constraints
   * @return {Object}  media constraints
   */
  function browserConstraintsWorkaround (constraints) {
    /**
     * Firefox workaround.
     *
     * Issue Summary: Browser behaviour for prompting the user for device
     *    permissions is not consistent for consecutive gUM calls. The following
     *    workaround makes it consistent if the less-specific constraint is
     *    provided.
     *
     * Context: The `deviceId` constraint format can be either a string or an
     *    object. The object format allows precise control over how the browser
     *    should try to get the track. It can be specified to fail if it can't
     *    fulfill the constraint (exact), or whether the constraint is "best
     *    effort" (ideal). When the string format is given, it follows the
     *    "ideal" behaviour.
     * See: https://developer.mozilla.org/en-US/docs/Web/API/ConstrainDOMString
     *
     *  - When the "ideal" behaviour is followed and the user has not given
     *      permission (and not disallowed), the browser will prompt the user
     *      for permission.
     *  - [Issue] When the "ideal" behaviour is followed and the user has given
     *      permission previously, the browser will not prompt the user for
     *      permission.
     *
     * Problem: When gUM is called for a different device (ideal behaviour) than
     *    permission was granted for previously, the browser will not prompt the
     *    user to give permission for the (new) device. This results in gUM
     *    using the original device for the new track.
     *
     * This happens on Firefox because the prompt for media permissions is
     *    specific to a single device. For Chrome, the prompt is generic to any
     *    device.
     * If permissions are always allowed ("remember my decision" on Firefox or
     *    default on Chrome), this issue wouldn't be seen.
     *
     * Workaround: When the behaviour (idea / exact) isn't specified, default
     *    to exact (instead of letting the browser default to ideal; as per the
     *    specification).
     *
     * This ensures that the browser always prompts the user to give permissions
     *    for the device (if not already granted). It is also more in-line with
     *    what might be expected when you ask for a certain device (ie. don't
     *    return a different device unless it was specified that it's okay).
     */
    for (const kind in constraints) {
      if (
        constraints[kind] &&
        typeof constraints[kind] === 'object' &&
        typeof constraints[kind].deviceId === 'string'
      ) {
        // Don't allow a "bare" value be provided for deviceId. Change it to use
        //    "exact".
        const id = constraints[kind].deviceId
        constraints[kind].deviceId = { exact: id }
      }
    }
    return constraints
  }

  /**
   * Wraps native mediaStream, adds tracks to trackManager and Media, and sets up event handlers on a given media.
   * @method setupMedia
   * @param {MediaStream} mediaStream Creating a Media object with it.
   * @return {Media}
   */
  function setupMedia (mediaStream) {
    const media = new Media(mediaStream, true)
    log.debug(`Creating Media with ID: ${media.id}.`)

    // Only add tracks to a Media objects using the `addTrack` method.
    // Specify that this is a local track we're adding
    mediaStream.getTracks().forEach(nativeTrack => {
      const wrappedTrack = trackManager.add(nativeTrack, mediaStream, true)

      media.addTrack(wrappedTrack)
    })

    media.once('media:stopped', mediaId => {
      remove(mediaId)
    })

    media.on('track:ended', ({ mediaId, trackId }) => {
      if (media.getTracks().length === 0) {
        remove(mediaId)
      }
    })

    media.on('track:muted', ({ mediaId, trackId }) => {
      // TBD: Add any extra behaviour (if needed) here
    })

    return media
  }

  /**
   * Create a new local Media object.
   * Use the provided constraints to get user media as the base MediaStream.
   * @method createLocal
   * @param  {MediaStreamConstraints}  constraints
   * @return {Promise}
   */
  function createLocal (constraints) {
    const constraintsWorkaround = browserConstraintsWorkaround(constraints)

    // Get user media, ...
    return new Promise((resolve, reject) => {
      // TODO: Proper error checking.
      // TODO: Use the WebAPI directly here? Probably not.
      navigator.mediaDevices
        .getUserMedia(constraintsWorkaround)
        .then(mediaStream => {
          const media = setupMedia(mediaStream)

          medias.set(media.id, media)
          // TODO: Better event. Include metadata?
          emitter.emit('media:new', media.id)

          resolve(media)
        })
        .catch(reject)
    })
  }

  /**
   * Creates a new local Screen Media object.
   * Use the provided constraints to get user media as the base MediaStream.
   * @method createLocalScreen
   * @param {MediaStreamConstraints} constraints
   * @return {promise}
   */
  function createLocalScreen (constraints) {
    const constraintsWorkaround = browserConstraintsWorkaround(constraints)

    return new Promise((resolve, reject) => {
      navigator.mediaDevices
        .getDisplayMedia(constraintsWorkaround)
        .then(mediaStream => {
          const media = setupMedia(mediaStream)

          medias.set(media.id, media)
          // TODO: Better event. Include metadata?
          emitter.emit('media:new', media.id)

          resolve(media)
        })
        .catch(reject)
    })
  }

  /**
   * Create a new remote Media object.
   * Use provided stream/tracks as the base media objects.
   * @method createRemote
   * @param  {MediaStream} mediaStream Native MediaStream object.
   * @param  {Tracks[]} tracks Array of Track objects.
   * @return {Media}
   */
  function createRemote (stream, tracks = []) {
    const media = new Media(stream, false)

    tracks.forEach(track => {
      media.addTrack(track)
    })

    media.once('media:stopped', mediaId => {
      remove(mediaId)
    })

    media.on('track:ended', ({ mediaId, trackId }) => {
      if (media.getTracks().length === 0) {
        remove(mediaId)
      }
    })

    medias.set(media.id, media)
    // TODO: Better event. Include metadata?
    emitter.emit('media:new', media.id)
    return media
  }

  /**
   * Removes a Media object from the medias array.
   * @private
   * @method remove
   * @param {String} mediaId
   */
  function remove (mediaId) {
    const media = get(mediaId)
    if (media) {
      medias.delete(mediaId)
      emitter.emit('media:removed', mediaId)
    }
  }

  /**
   * Retrieve an existing Media object.
   * @method get
   * @param  {String} mediaId ID of the desired Media object.
   * @return {Media}
   */
  function get (mediaId) {
    const media = medias.get(mediaId)
    if (!media) {
      log.debug(`No media found with ID: ${mediaId}.`)
    }
    return media
  }

  /**
   * Retrieve a list of all existing Media object's IDs.
   * @method getAll
   * @return {Array} List of Media IDs.
   */
  function getAll () {
    return Array.from(medias.keys())
  }

  /**
   * Finds the Media object that contains the specified Track.
   * @method findTrack
   * @param  {string}  trackId The ID of the Track to find.
   * @return {string} The ID of the Media object that contains the Track.
   */
  function findTrack (trackId) {
    // Search through all Media objects for the one that has the desired track.
    const allMedia = Array.from(medias.values())
    const media = allMedia.find(media => media.getTrack(trackId))
    if (media) {
      log.debug(`Found Media (${media.id}) with Track ${trackId}.`)
      return media.id
    } else {
      log.debug(`Found no Media with Track ${trackId}.`)
    }
  }

  function on (...args) {
    return emitter.on(...args)
  }

  function once (...args) {
    return emitter.once(...args)
  }

  function off (...args) {
    return emitter.off(...args)
  }

  /**
   * The exposed API.
   */
  return {
    // Getter APIs.
    get,
    getAll,
    getState,
    findTrack,
    // Create APIs.
    createLocal,
    createLocalScreen,
    createRemote,
    // Event APIs.
    on,
    once,
    off
  }
}
